import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Download, Calendar } from 'lucide-react';
import { API_URL } from '../../config';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function TaxOverview({ adminData }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(null); // null = whole year
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [year, month]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const params = new URLSearchParams({ year: year.toString() });
      if (month) params.append('month', month.toString());
      
      const res = await fetch(`${API_URL}/api/tax/overview/monthly?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch tax overview');
      
      const result = await res.json();
      setData(result);
    } catch (error) {
      console.error('Error fetching tax overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('snapshot_token');
      const params = new URLSearchParams({ year: year.toString() });
      if (month) params.append('month', month.toString());
      
      const res = await fetch(`${API_URL}/api/tax/export/zip?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Export failed');
      
      const result = await res.json();
      
      // Download ZIP
      const blob = new Blob([Uint8Array.from(atob(result.data), c => c.charCodeAt(0))], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      
      alert('Steuerexport erfolgreich heruntergeladen!');
    } catch (error) {
      console.error('Error exporting tax data:', error);
      alert('Fehler beim Export');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const aggregated = data?.data?.reduce((acc, m) => ({
    revenue_brutto: acc.revenue_brutto + m.revenue.brutto,
    revenue_netto: acc.revenue_netto + m.revenue.netto,
    ust: acc.ust + m.revenue.ust,
    expenses_total: acc.expenses_total + m.expenses.total,
    vst: acc.vst + m.expenses.vst,
    profit_loss: acc.profit_loss + m.profit_loss,
    ust_zahllast: acc.ust_zahllast + m.ust_zahllast
  }), {
    revenue_brutto: 0,
    revenue_netto: 0,
    ust: 0,
    expenses_total: 0,
    vst: 0,
    profit_loss: 0,
    ust_zahllast: 0
  }) || {};

  return (
    <div className="min-h-screen bg-black grain-texture text-white p-4 md:p-6 overflow-x-hidden">
      <div className="max-w-7xl mx-auto w-full">
        
        <div className="mb-8">
          <div className="mb-4">
            <div className="flex items-center gap-3 md:gap-4 mb-2">
              <DollarSign className="w-6 h-6 md:w-8 md:h-8 text-[#10B981]" />
              <h1 className="text-2xl md:text-4xl font-bold">Steuer-Übersicht</h1>
            </div>
            <p className="text-white/60 text-sm">Einnahmen, Ausgaben & Steuerlast</p>
          </div>
          
          <button
            onClick={handleExport}
            disabled={exporting}
            data-testid="tax-export-button"
            className="w-full md:w-auto px-6 py-3 bg-[#10B981] text-black rounded-xl font-bold hover:bg-[#059669] transition-all flex items-center justify-center gap-2"
          >
            <Download className="w-5 h-5" />
            {exporting ? 'Wird erstellt...' : '📤 Steuerexport (ZIP)'}
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">Jahr</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-white/5 border-2 border-white/20 rounded-xl text-white"
              data-testid="year-filter"
            >
              {[2024, 2025, 2026].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">Monat (optional)</label>
            <select
              value={month || ''}
              onChange={(e) => setMonth(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-4 py-2 bg-white/5 border-2 border-white/20 rounded-xl text-white"
              data-testid="month-filter"
            >
              <option value="">Ganzes Jahr</option>
              {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{new Date(2025, m-1).toLocaleDateString('de-DE', {month: 'long'})}</option>
              ))}
            </select>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="p-4 md:p-6 bg-black border-2 border-white/10 rounded-xl grain-texture">
            <div className="text-xs md:text-sm text-white/60 mb-2">Einnahmen (Netto)</div>
            <div className="text-2xl md:text-3xl font-bold text-[#10B981]">€{aggregated.revenue_netto?.toFixed(2)}</div>
          </div>
          
          <div className="p-4 md:p-6 bg-black border-2 border-white/10 rounded-xl grain-texture">
            <div className="text-xs md:text-sm text-white/60 mb-2">Ausgaben (Total)</div>
            <div className="text-2xl md:text-3xl font-bold text-red-400">€{aggregated.expenses_total?.toFixed(2)}</div>
          </div>
          
          <div className="p-4 md:p-6 bg-black border-2 border-white/10 rounded-xl grain-texture">
            <div className="text-xs md:text-sm text-white/60 mb-2">Gewinn/Verlust</div>
            <div className={`text-2xl md:text-3xl font-bold ${aggregated.profit_loss >= 0 ? 'text-[#10B981]' : 'text-red-500'}`}>
              €{aggregated.profit_loss?.toFixed(2)}
            </div>
          </div>
          
          <div className="p-4 md:p-6 bg-black border-2 border-white/10 rounded-xl grain-texture">
            <div className="text-xs md:text-sm text-white/60 mb-2">USt-Zahllast</div>
            <div className="text-2xl md:text-3xl font-bold text-orange-400">€{aggregated.ust_zahllast?.toFixed(2)}</div>
          </div>
        </div>

        {/* Chart */}
        {data?.data && data.data.length > 0 && (
          <div className="p-4 md:p-6 bg-black border-2 border-white/10 rounded-xl grain-texture mb-8">
            <h2 className="text-xl md:text-2xl font-bold mb-6">Einnahmen vs. Ausgaben</h2>
            
            {/* Desktop Chart */}
            <div className="hidden md:block">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={data.data.map(m => ({
                  name: `${m.month}/${m.year}`,
                  Einnahmen: m.revenue.netto,
                  Ausgaben: m.expenses.total,
                  Gewinn: m.profit_loss
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff15" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#ffffff60" 
                    tick={{ fill: '#ffffff80', fontSize: 12 }} 
                    tickLine={{ stroke: '#ffffff40' }}
                  />
                  <YAxis 
                    stroke="#ffffff60" 
                    tick={{ fill: '#ffffff80', fontSize: 12 }} 
                    tickLine={{ stroke: '#ffffff40' }}
                    tickFormatter={(value) => `€${value}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#000', 
                      border: '2px solid #10B981', 
                      borderRadius: '12px', 
                      color: '#fff',
                      padding: '12px'
                    }}
                    labelStyle={{ color: '#10B981', fontWeight: 'bold', marginBottom: '8px' }}
                    itemStyle={{ padding: '4px 0' }}
                    formatter={(value) => `€${value.toFixed(2)}`}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="circle"
                    iconSize={12}
                  />
                  <Bar dataKey="Einnahmen" fill="#10B981" radius={[8, 8, 0, 0]} barSize={60} />
                  <Bar dataKey="Ausgaben" fill="#ef4444" radius={[8, 8, 0, 0]} barSize={60} />
                  <Bar dataKey="Gewinn" fill="#3b82f6" radius={[8, 8, 0, 0]} barSize={60} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Mobile: Karten statt Chart */}
            <div className="md:hidden space-y-4">
              {data.data.map((m, idx) => (
                <div 
                  key={idx}
                  className="bg-white/5 border border-white/10 rounded-lg p-4"
                >
                  <div className="font-bold text-lg mb-4 pb-3 border-b border-white/10">
                    {new Date(m.year, m.month - 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
                        <span className="text-white/70">Einnahmen</span>
                      </div>
                      <span className="font-bold text-[#10B981]">€{m.revenue.netto.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <span className="text-white/70">Ausgaben</span>
                      </div>
                      <span className="font-bold text-red-400">€{m.expenses.total.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <span className="text-white/70 font-medium">Gewinn</span>
                      </div>
                      <span className={`font-bold text-lg ${m.profit_loss >= 0 ? 'text-blue-400' : 'text-red-500'}`}>
                        €{m.profit_loss.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
